import { motion } from 'framer-motion';
import { Cpu, Bot, Factory, Leaf, Network, Box } from 'lucide-react';

const Tracks = () => {
  const tracks = [
    {
      icon: Cpu,
      title: 'Edge AI and Real-Time Automation',
      topics: [
        'Edge analytics',
        'AI deployment on embedded hardware',
        'Energy-efficient edge computing',
      ],
    },
    {
      icon: Bot,
      title: 'AI-Powered Autonomous Systems',
      topics: [
        'Autonomous navigation',
        'Simulation and validation',
        'Autonomous vehicles and drones',
      ],
    },
    {
      icon: Network,
      title: 'AI and Robotics',
      topics: [
        'Swarm robotics',
        'Human-robot collaboration',
        'Industrial robotics',
      ],
    },
    {
      icon: Factory,
      title: 'Smart Manufacturing & Industry 5.0',
      topics: [
        'Cyber-physical systems',
        'AI-driven smart factories',
        'Predictive maintenance',
      ],
    },
    {
      icon: Leaf,
      title: 'Intelligent Systems for Sustainability',
      topics: [
        'Multi-agent systems',
        'Self-learning algorithms',
        'Sustainable automation',
      ],
    },
    {
      icon: Box,
      title: 'Digital Twins',
      topics: [
        'AI-enabled digital twin systems',
        'Digital twin architectures',
        'Real-time monitoring and simulation',
      ],
    },
    {
      icon: Leaf,
      title: 'AI for Green Automation',
      topics: [
        'AI for environmental monitoring',
        'Renewable energy automation',
        'Sustainable industrial processes',
      ],
    },
    {
      icon: Box,
      title: 'Smart Materials',
      topics: [
        'Smart sensors and actuators',
        'Aerospace applications',
        'Biomedical applications',
      ],
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 },
  };

  return (
    <div data-testid="tracks-page">
      {/* Hero Section */}
      <section className="py-12 md:py-12 mesh-gradient relative overflow-hidden" data-testid="tracks-hero">
        <div className="absolute inset-0 bg-black/5 grid-pattern"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div {...fadeInUp} className="max-w-4xl">
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-950 mb-6 tracking-tight">
              Conference <span className="text-[#B6671D]">Themes & Tracks</span>
            </h1>
            <p className="font-inter text-lg text-slate-600 leading-relaxed">
              Explore diverse research areas covering the latest advancements in robotics, automation, AI, and smart manufacturing technologies.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tracks Grid - Bento Style */}
      <section className="py-20 bg-white relative" data-testid="tracks-grid">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tracks.map((track, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-[#B6671D]/50 transition-all duration-300 group relative"
                data-testid={`track-card-${index}`}
              >
                {/* Card Header */}
                <div className="bg-slate-50 p-6 border-b border-slate-200">
                  <div className="w-14 h-14 bg-[#B6671D] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <track.icon className="text-white" size={28} />
                  </div>
                  <h3 className="font-outfit text-xl font-bold text-slate-950 leading-tight">
                    {track.title}
                  </h3>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <ul className="space-y-3">
                    {track.topics.map((topic, topicIndex) => (
                      <li key={topicIndex} className="flex items-start space-x-3">
                        <div className="w-1.5 h-1.5 bg-[#B6671D] rounded-full mt-2 flex-shrink-0"></div>
                        <span className="font-inter text-sm text-slate-600">{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Hover Effect Border */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#B6671D]/20 rounded-xl transition-all pointer-events-none"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Topics */}
      <section className="py-20 bg-slate-50" data-testid="additional-topics">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto">
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-950 mb-8 text-center">
              And More...
            </h2>
            <p className="font-inter text-slate-600 leading-relaxed text-center mb-8">
              The conference welcomes papers on topics including, but not limited to, robotics, automation, artificial intelligence, smart manufacturing, aerospace systems, electric mobility, drone technologies, and other emerging areas aligned with the conference theme
            </p>
            <div className="bg-[#B6671D]/10 border-l-4 border-[#B6671D] p-6 rounded-r-lg">
              <p className="font-inter text-slate-700">
                <strong>Note:</strong> Authors are encouraged to submit original research, review articles, and case
                studies that contribute to advancing knowledge and innovation in these critical areas.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Tracks;